import { AbstractControl, ControlValueAccessor, FormControl, NgControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  Self,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SearchCountryField, CountryISO, PhoneNumberFormat } from 'ngx-intl-tel-input-gg';
import { isValidE164 } from '@app/shared/utils/phone.util';

/** The parts of `ngx-intl-tel-input`'s change object read here; the library doesn't export its type. */
interface IntlTelChange {
  number?: string;
  e164Number?: string;
}

@Component({
    selector: 'c-phone',
    templateUrl: './phone.component.html',
    styleUrls: ['./phone.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class PhoneComponent implements ControlValueAccessor, OnChanges, OnInit {
  private readonly destroyRef = inject(DestroyRef);

  @Input() label: string | undefined;
  @Input() hidden: boolean | undefined;
  @Input() disabled: boolean;
  @Input() errorPlacement: string | undefined;
  @Input() info: string | undefined;
  @Input() value: string | undefined;
  @Input() formInline: boolean;
  @Input() inline: boolean;
  @Input() uppercase: boolean;
  @Input() showError = true;
  @Input() noBorder: boolean;
  @Input() preferredCountries: CountryISO[] | undefined;
  @Input() selectedCountry: CountryISO | undefined;

  @Output() action: EventEmitter<any> = new EventEmitter();
  @Output() keyenter: EventEmitter<any> = new EventEmitter();
  // eslint-disable-next-line @angular-eslint/no-output-native
  @Output() blur: EventEmitter<any> = new EventEmitter();

  separateDialCode = false;
  SearchCountryField = SearchCountryField;
  CountryISO = CountryISO;
  PhoneNumberFormat = PhoneNumberFormat;

  readonly phoneControl = new FormControl<IntlTelChange | string | null>(null);

  private currentValue: string | null = null;

  private readonly phoneValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null =>
    !control.value || isValidE164(control.value) ? null : { validatePhoneNumber: { valid: false } };

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private cd: ChangeDetectorRef
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    const control = this.control;
    if (control) {
      control.addValidators(this.phoneValidator);
      control.updateValueAndValidity({ emitEvent: false });
      this.destroyRef.onDestroy(() => control.removeValidators(this.phoneValidator));
    }

    this.phoneControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((change) => {
      const value = this.toE164(change);
      if (value === this.currentValue) {
        return;
      }
      this.currentValue = value;
      this.onChange(value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['disabled'] && this.control) {
      changes['disabled'].currentValue === true ? this.control.disable() : this.control.enable();
      this.cd.markForCheck();
    }
  }

  get control() {
    return this.ngControl?.control as FormControl;
  }

  writeValue(value: string | null): void {
    this.currentValue = value || null;
    this.phoneControl.setValue(value || null, { emitEvent: false });
  }

  onChange = (value: string | null) => {};
  onTouched = () => {};

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: () => {}): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.phoneControl.disable({ emitEvent: false }) : this.phoneControl.enable({ emitEvent: false });
    this.cd.markForCheck();
  }

  private toE164(change: IntlTelChange | string | null): string | null {
    if (!change) {
      return null;
    }
    if (typeof change === 'string') {
      return change;
    }
    return change.e164Number || change.number || null;
  }
}
